import type { Journal, JournalEntry } from './engine.ts';

/**
 * The upload resume journal (roadmap 5.3). It lives in its own IndexedDB database, `mdt-uploads`, so a tab
 * that is killed can resume. In shared-computer mode nothing is kept (spec §1): the journal is in memory
 * only. Sign-out wipes the database with the rest (lib/wipe.ts).
 */
export const UPLOAD_DB = 'mdt-uploads';

let dbPromise: Promise<IDBDatabase> | null = null;

function openJournalDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(UPLOAD_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore('uploads', { keyPath: 'uploadId' });
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
  return dbPromise;
}

async function store<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openJournalDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction('uploads', mode);
    const r = fn(tx.objectStore('uploads'));
    tx.oncomplete = () => resolve(r.result);
    tx.onerror = () => reject(tx.error);
  });
}

const indexedJournal: Journal = {
  async find(personId, targetKey, fingerprint) {
    const all = await store<JournalEntry[]>('readonly', (s) => s.getAll() as IDBRequest<JournalEntry[]>);
    return all.find((e) => e.personId === personId && e.targetKey === targetKey && e.fingerprint === fingerprint) ?? null;
  },
  async forTarget(personId, targetKey) {
    const all = await store<JournalEntry[]>('readonly', (s) => s.getAll() as IDBRequest<JournalEntry[]>);
    return all.filter((e) => e.personId === personId && e.targetKey === targetKey);
  },
  async save(e) {
    await store('readwrite', (s) => s.put(e));
  },
  async remove(uploadId) {
    await store('readwrite', (s) => s.delete(uploadId));
  },
};

const memory = new Map<string, JournalEntry>();
const memoryJournal: Journal = {
  find: async (p, t, f) => [...memory.values()].find((e) => e.personId === p && e.targetKey === t && e.fingerprint === f) ?? null,
  forTarget: async (p, t) => [...memory.values()].filter((e) => e.personId === p && e.targetKey === t),
  save: async (e) => void memory.set(e.uploadId, e),
  remove: async (id) => void memory.delete(id),
};

/** The journal for this session: persistent in standard mode, in memory on a shared computer. */
export const journalFor = (mode: 'standard' | 'shared'): Journal => (mode === 'shared' || !('indexedDB' in globalThis) ? memoryJournal : indexedJournal);

/** Sign-out (lib/wipe.ts): the journal goes with everything else. */
export function deleteUploadJournal(): Promise<void> {
  memory.clear();
  const close = dbPromise?.then((db) => db.close()).catch(() => {});
  dbPromise = null;
  return Promise.resolve(close).then(() => new Promise<void>((done) => {
    if (!('indexedDB' in globalThis)) return done();
    const del = indexedDB.deleteDatabase(UPLOAD_DB);
    del.onsuccess = del.onerror = del.onblocked = () => done();
  }));
}
